import React, { useState, useEffect, useContext } from 'react';
import { generateBase64String, formatPhoneForStorage, formatUGX } from "./UtilityFunctions";
import fields from './fields';
import { GlobalContext } from "./GlobalContext";
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Form from '@radix-ui/react-form';
import * as Tabs from '@radix-ui/react-tabs';
import { Button } from '@radix-ui/themes';
import { TextInput } from '@tremor/react';
import { Card, Title, Text } from '@tremor/react';
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Clock } from 'lucide-react';
import socket from './socket';
import { apiFetch } from './api.js';
import { ICON_SIZE, SignInIcon } from "./actionIcons";

//Shared formatter — see formatUGX in UtilityFunctions.jsx (was en-UG "USh").
const formatCurrency = (amount) => formatUGX(amount);

/**
 * What the signup wizard should do about the email-availability check.
 *
 * Exported ONLY so it can be tested. The rule lived inline in nextPrev as
 * `if (!isUnique.EmailAddress) return;` — a single falsy test that silently
 * conflated three different situations: never checked, still checking, and
 * already has an account. The third is not an error. Since migration 057 a
 * known address is LINKED TO rather than refused, and the invite token emailed
 * to it is what authorises that. But the guard stopped the wizard dead with no
 * message, so Next did nothing and the reason was left to guess.
 *
 * @param status isUnique.EmailAddress: '' | 'Pending' | 'existing' | true | false
 * @returns 'check'    never looked — run the availability check and wait
 *          'wait'     a check is in flight
 *          'blocked'  genuinely unavailable (unreachable today: the server
 *                     reports existence, and existence is now allowed)
 *          'existing' already has an account — PROCEED, and skip the password
 *          'proceed'  a new address
 */
export const emailCheckGate = (status) => {
  if (status === '' || status === undefined || status === null) return 'check';
  if (status === 'Pending') return 'wait';
  if (status === 'existing') return 'existing';
  if (status === false) return 'blocked';
  return 'proceed';
};

function RegistrationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token") || "";
  const inviteEmail = searchParams.get("email") || "";
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);

  const { toggleMessageBox } = useContext(GlobalContext);
  
  const [currentTab, setCurrentTab] = useState(0);
  const [companyInfo, setCompanyInfo] = useState({
    DealershipName: '',
    CompanyPhone1: '',
    CompanyPhone2: '',
    CompanyAddress: '',
  });
  
  const [personalInfo, setPersonalInfo] = useState({
    FirstName: '',
    LastName: '',
    Phone1: '',
    Phone2: '',
  });
  
  const [loginInfo, setLoginInfo] = useState({
    EmailAddress: '',
    Password: '',
    ConfirmPassword: '',
  });
  
  const [errors, setErrors] = useState(companyInfo);
  const [isUnique, setIsUnique] = useState({
    DealershipName: '',
    EmailAddress: '',
  });
  
  const DealershipID = generateBase64String();

  const validatePassword = () => {
    if (!loginInfo.Password) {
      setErrors(prev => ({ ...prev, Password: "Please enter a password" }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, Password: "" }));
    }
    
    if (loginInfo.Password.length < 6) {
      setErrors(prev => ({ ...prev, Password: "Password is too short" }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, Password: "" }));
    }
    
    if (loginInfo.Password !== loginInfo.ConfirmPassword) {
      setErrors(prev => ({ ...prev, ConfirmPassword: "Passwords do not match" }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, ConfirmPassword: "" }));
    }
    
    return true;
  };

  const validateForm = (info) => {
    let tempErrors = {};
    let hasErrors = false;
    let regex;
    
    for (let attr of Object.keys(info)) {
      // Check for whether entered values are valid
      if (fields[attr] && fields[attr].regex) {
        regex = new RegExp(fields[attr].regex);
        if (!regex.test(info[attr])) {
          tempErrors[attr] = fields[attr].message;
          hasErrors = true;
        } else {
          tempErrors[attr] = '';
        }
      }

      // Check for whether required fields are filled in
      if (fields[attr] && fields[attr].required && info[attr] === "") {
        tempErrors[attr] = fields[attr].displayName + " is required";
        hasErrors = true;
      } else {
        if (!tempErrors[attr]) tempErrors[attr] = '';
      }
    }

    setErrors(prev => ({ ...prev, ...tempErrors }));
    return !hasErrors;
  };

  const validateSingleField = (name, value) => {
    let tempErrors = { ...errors };
    let regex;

    if (fields[name]) {
      // Check for whether entered values are valid (if a value exists)
      if (fields[name].regex && value !== "") {
        regex = new RegExp(fields[name].regex);
        if (!regex.test(value)) {
          tempErrors[name] = fields[name].message;
        } else {
          tempErrors[name] = '';
        }
      }
      
      // Check for whether required fields are filled in
      if (fields[name].required && value === "") {
        tempErrors[name] = fields[name].displayName + " is required";
      } else if (!tempErrors[name]) {
        tempErrors[name] = '';
      }
    }
    setErrors(tempErrors);
  };

  const handleBlur = ({ target }) => {
    const { name, value } = target;
    
    switch (name) {
      case "DealershipName":
        socket.emit('check-dealership-name', { DealershipName: companyInfo.DealershipName });
        setIsUnique(prev => ({ ...prev, DealershipName: 'Pending' }));
        break;
      case "EmailAddress":
        socket.emit('check-emailaddress', { EmailAddress: loginInfo.EmailAddress });
        setIsUnique(prev => ({ ...prev, EmailAddress: 'Pending' }));
        break;
      default:
    }
    
    validateSingleField(name, value);
  };

  const nextPrev = (n) => {
    if (currentTab === 0) {
      if (!validateForm(companyInfo)) return;
      if (isUnique.DealershipName === 'Pending') return;
      if (!isUnique.DealershipName) return;
    }

    if (currentTab === 1) {
      if (!selectedTier) {
        toggleMessageBox({
          on: true,
          title: "Select a Tier",
          message: "Please select a subscription tier to continue.",
          buttons: ["OK"]
        });
        return;
      }
    }    
    
    if (currentTab === 2 && !validateForm(personalInfo)) return;
    
    if (currentTab === 3) {
      if (!validateForm(loginInfo)) return;

      const gate = emailCheckGate(isUnique.EmailAddress);
      if (gate === 'check') {
        //Never looked — kick the check off and let the clock indicator show why
        //nothing moved. Pressing Next again once it lands proceeds.
        socket.emit('check-emailaddress', { EmailAddress: loginInfo.EmailAddress });
        setIsUnique(prev => ({ ...prev, EmailAddress: 'Pending' }));
        return;
      }
      if (gate === 'wait' || gate === 'blocked') return;
      //'existing' skips the password: the server leaves the stored hash alone,
      //so anything typed here would be discarded, and the fields are hidden.
      if (gate !== 'existing' && !validatePassword()) return;
    }

    const newTab = currentTab + n;
    if (newTab >= 4) {
      if (emailCheckGate(isUnique.EmailAddress) !== 'existing' && !validatePassword()) return;
      
      // Submit the form
      apiFetch('/newdealership', { body: {
          InviteToken: inviteToken,
          DealershipID: DealershipID,
          FirstName: personalInfo.FirstName,
          LastName: personalInfo.LastName,
          Phone1: formatPhoneForStorage(personalInfo.Phone1),  
          Phone2: formatPhoneForStorage(personalInfo.Phone2),  
          EmailAddress: loginInfo.EmailAddress,
          UserPassword: loginInfo.Password,
          Dealership: {
            DealershipID: DealershipID,
            CompanyName: companyInfo.DealershipName,
            ContactPhone1: formatPhoneForStorage(companyInfo.CompanyPhone1),  
            ContactPhone2: formatPhoneForStorage(companyInfo.CompanyPhone2),  
            BondName: companyInfo.CompanyAddress,
            TierID: selectedTier,
            // New fields with default values
            FoundingYear: companyInfo.FoundingYear || new Date().getFullYear(), // Default to current year
            Mission: companyInfo.Mission || "Providing exceptional automotive services to our customers",
            History: companyInfo.History || "Our dealership was established to serve the local community with quality vehicles and outstanding service.",
            CustomerSatisfaction: companyInfo.CustomerSatisfaction || 95, // Default to 95%
            TeamSize: companyInfo.TeamSize || 10 // Default to 10 team members
          }
        } })
      .then(async (response) => {
        const resObj = await response.json();
        
        if (response.ok) {
          if (resObj.ok) {
            //The server reports whether it linked this dealership to a
            //credential that already existed. Saying "please login" without
            //that distinction is what leaves someone hunting for a password
            //they were never asked to set.
            toggleMessageBox({
              on: true,
              title: "Dealership Created.",
              message: resObj.usedExistingCredential
                ? `Dealership created and added to your existing account. Sign in with `
                  + `${loginInfo.EmailAddress} and the password you already use — it has not changed.`
                : "Dealership successfully created. Please Login.",
              buttons: ["OK"],
              clicked: ""
            });
            navigate("/login");
          } else {
            toggleMessageBox({
              on: true,
              title: "Error",
              message: "Error creating dealership. Please check your input data and try again.",
              buttons: ["OK"],
              clicked: ""
            });
          }
        } else {
          toggleMessageBox({
            on: true,
            title: "Error",
            message: "Error creating dealership. Please contact support.",
            buttons: ["OK"],
            clicked: ""
          });
        }
      }).catch((err) => {
        toggleMessageBox({
          on: true,
          title: "Connection Error",
          message: "Error creating dealership. Please check your network connection and try again.",
          buttons: ["OK"],
          clicked: ""
        });
      });
    } else {
      setCurrentTab(newTab);
    }
  };

  const handleInputChange = (e) => {
    if (currentTab === 0) {
      setCompanyInfo({
        ...companyInfo,
        [e.target.name]: e.target.value
      });
    } else if (currentTab === 2) {
      setPersonalInfo({
        ...personalInfo,
        [e.target.name]: e.target.value
      });
    } else if (currentTab === 3) {
      setLoginInfo({
        ...loginInfo,
        [e.target.name]: e.target.value
      });
    }
  };

  useEffect(() => {
    // Connect if not connected
    if (!socket.connected) {
      socket.connect();
    }
    //Add listeners
    socket.on('dealership-name-exists', () => {
      setIsUnique(prev => ({ ...prev, DealershipName: false }));
    });
    
    socket.on('dealership-name-new', () => {
      setIsUnique(prev => ({ ...prev, DealershipName: true }));
    });

    socket.on('check-dealership-name-error', (msg) => {
      console.log(msg);
    });

    //'existing', not false. Since the identity split an address that already
    //has an account is NOT a collision: the signup links this new dealership to
    //the credential that address already owns, and the invite token emailed to
    //it is what proves the person asking controls the inbox. The server has
    //supported this since 057 (addDealership find-or-creates the Identity); the
    //form was the only thing still refusing.
    socket.on('emailaddress-exists', () => {
      setIsUnique(prev => ({ ...prev, EmailAddress: 'existing' }));
    });

    socket.on('emailaddress-new', () => {
      setIsUnique(prev => ({ ...prev, EmailAddress: true }));
    });

    socket.on('check-emailaddress-error', (msg) => {
      console.log(msg);
    });

    return () => {
      socket.removeAllListeners();
      };
  }, []);

  //Fetch tiers on component mount
  useEffect(() => {
    apiFetch('/dealerships/tiers', { method: "GET" })
    .then(res => res.json())
    .then(data => {
      // The response format is different - it's a direct array, not { ok: true, data: [] }
      if (Array.isArray(data)) {
        setTiers(data);
        // Set Free tier as default
        const freeTier = data.find(t => t.TierName === 'Free');
        if (freeTier) setSelectedTier(freeTier.TierID);
      }
    })
    .catch(err => console.error('Error fetching tiers:', err));
  }, []); 

  // Status indicator component for unique checks
  const UniqueStatusIndicator = ({ field, status }) => {
    if (status === true) return <CheckCircle className="inline ml-2" size={16} color="green" />;
    //'existing' is not an error — the account is recognised, and signup
    //continues onto it. A red cross here is what made this look like a refusal.
    if (status === 'existing') return <CheckCircle className="inline ml-2" size={16} color="blue" />;
    if (status === false) return <XCircle className="inline ml-2" size={16} color="red" />;
    if (status === 'Pending') return <Clock className="inline ml-2" size={16} color="orange" />;
    return null;
  };

  // Error message component
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return <Text color="red" className="text-sm mt-1">{error}</Text>;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <style>{`
        .tier-scroll-container::-webkit-scrollbar {
          width: 8px;
        }
        .tier-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .tier-scroll-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .tier-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>      
      <Card className="max-w-2xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <Title className="text-center">Register</Title>
          <Button variant="ghost" onClick={() => navigate("/login")}><SignInIcon size={ICON_SIZE} />
            Login
          </Button>
        </div>

        <Tabs.Root value={`tab-${currentTab}`}>
          <Tabs.List className="flex justify-center mb-6 gap-2" aria-label="Registration steps">
            {['Company Info', 'Select Tier', 'User Info', 'Login Info'].map((label, index) => (
              <Tabs.Trigger
                key={index}
                value={`tab-${index}`}
                className={`px-4 py-2 rounded-full text-sm ${currentTab === index
                  ? 'bg-[var(--theme-accent)] text-[var(--theme-accent-fg)]'
                  : 'bg-[var(--theme-surface-alt)] text-[var(--theme-muted)]'}`}
                disabled
              >
                {label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Tabs.Content value={`tab-${currentTab}`}>
            <Form.Root className="space-y-6">
              {currentTab === 0 && (
                <div className="space-y-4">
                  
                  <Form.Field name="DealershipName">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Company Name</Form.Label>
                      <div className="flex items-center">
                        <ErrorMessage error={errors['DealershipName']} />
                        <UniqueStatusIndicator field="DealershipName" status={isUnique.DealershipName} />
                      </div>
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="DealershipName"
                        value={companyInfo.DealershipName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter company name"
                        error={!!errors['DealershipName'] || isUnique.DealershipName === false}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="CompanyAddress">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Address / Bond</Form.Label>
                      <ErrorMessage error={errors['CompanyAddress']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="CompanyAddress"
                        value={companyInfo.CompanyAddress}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter company address"
                        error={!!errors['CompanyAddress']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="CompanyPhone1">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Contact Phone 1</Form.Label>
                      <ErrorMessage error={errors['CompanyPhone1']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="CompanyPhone1"
                        value={companyInfo.CompanyPhone1}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter primary contact phone"
                        error={!!errors['CompanyPhone1']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="CompanyPhone2">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Contact Phone 2 (Optional)</Form.Label>
                      <ErrorMessage error={errors['CompanyPhone2']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="CompanyPhone2"
                        value={companyInfo.CompanyPhone2}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter secondary contact phone"
                        error={!!errors['CompanyPhone2']}
                      />
                    </Form.Control>
                  </Form.Field>
                </div>
              )}

              {currentTab === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold mb-2">Choose Your Plan</h3>
                    <p className="text-sm text-gray-600">Select a tier based on the combined total number of vehicles you will have in Inventory + Credit (active, uncleared credit). <i>E.g 20 in Inventory + 15 in credit = 35, so pick "Silver"</i></p>
                  </div>
                  
                  {/* Add scrollable container with explicit height */}
                  <div className="tier-scroll-container max-h-[200px] overflow-y-auto pr-2" style={{ scrollbarGutter: 'stable' }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tiers.map((tier) => (
                        <div
                          key={tier.TierID}
                          onClick={() => setSelectedTier(tier.TierID)}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            selectedTier === tier.TierID
                              ? 'border-green-600 bg-green-50 shadow-md'
                              : 'border-gray-300 hover:border-green-400'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-lg font-semibold">{tier.TierName}</h4>
                            <div className="text-right">
                              <div className="text-xl font-bold text-green-600">
                                {tier.MonthlyPrice === 0 
                                  ? 'Free' 
                                  : formatCurrency(tier.MonthlyPrice)
                                }
                              </div>
                              {tier.MonthlyPrice > 0 && (
                                <div className="text-xs text-gray-500">/month</div>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-sm text-gray-700 space-y-1">
                            <p>
                              <span className="font-medium">Vehicles:</span>{' '}
                              {tier.MaxVehicles === 999999 
                                ? `${tier.MinVehicles}+` 
                                : `${tier.MinVehicles}-${tier.MaxVehicles}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              +{tier.OverageAllowance}% grace allowance before upgrade required
                            </p>
                          </div>
                          
                          {selectedTier === tier.TierID && (
                            <div className="mt-3 flex items-center text-green-600 text-sm">
                              <CheckCircle size={16} className="mr-1" />
                              Selected
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {currentTab === 2 && (
                <div className="space-y-4">
                  <Form.Field name="FirstName">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">First Name</Form.Label>
                      <ErrorMessage error={errors['FirstName']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="FirstName"
                        value={personalInfo.FirstName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter your first name"
                        error={!!errors['FirstName']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="LastName">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Last Name</Form.Label>
                      <ErrorMessage error={errors['LastName']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="LastName"
                        value={personalInfo.LastName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter your last name"
                        error={!!errors['LastName']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="Phone1">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Phone 1</Form.Label>
                      <ErrorMessage error={errors['Phone1']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="Phone1"
                        value={personalInfo.Phone1}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter your primary phone"
                        error={!!errors['Phone1']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="Phone2">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Phone 2 (Optional)</Form.Label>
                      <ErrorMessage error={errors['Phone2']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="Phone2"
                        value={personalInfo.Phone2}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter your secondary phone"
                        error={!!errors['Phone2']}
                      />
                    </Form.Control>
                  </Form.Field>
                </div>
              )}

              {currentTab === 3 && (
                <div className="space-y-4">                  
                  <Form.Field name="EmailAddress">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Email Address</Form.Label>
                      <div className="flex items-center">
                        <ErrorMessage error={errors['EmailAddress']} />
                        <UniqueStatusIndicator field="EmailAddress" status={isUnique.EmailAddress} />
                      </div>
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="EmailAddress"
                        value={loginInfo.EmailAddress}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        type="email"
                        placeholder="Enter your email address"
                        error={!!errors['EmailAddress'] || isUnique.EmailAddress === false}
                        disabled={isUnique.EmailAddress === 'existing'}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  {isUnique.EmailAddress === 'existing' ? (
                    /* This address already owns an account, so there is no
                       password to choose: addDealership links the new dealership
                       to the credential it already has and deliberately leaves
                       the stored hash alone. Showing password fields here would
                       invite someone to pick one that is then discarded — the
                       server reports usedExistingCredential precisely so the
                       client can say this instead. */
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                      <Text className="text-sm font-medium">You already have an Auto Dealer UG account</Text>
                      <Text as="p" className="text-sm mt-1">
                        This dealership will be added to the account you already use for{' '}
                        <strong>{loginInfo.EmailAddress}</strong>. Sign in with your existing
                        password — there is no new one to set, and your current password is
                        not changed.
                      </Text>
                    </div>
                  ) : (
                    <>
                  <Form.Field name="Password">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Password</Form.Label>
                      <ErrorMessage error={errors['Password']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="Password"
                        value={loginInfo.Password}
                        onChange={handleInputChange}
                        onBlur={() => validatePassword()}
                        type="password"
                        placeholder="Enter your password"
                        error={!!errors['Password']}
                      />
                    </Form.Control>
                  </Form.Field>
                  
                  <Form.Field name="ConfirmPassword">
                    <div className="flex justify-between items-center">
                      <Form.Label className="text-sm font-medium">Confirm Password</Form.Label>
                      <ErrorMessage error={errors['ConfirmPassword']} />
                    </div>
                    <Form.Control asChild>
                      <TextInput
                        name="ConfirmPassword"
                        value={loginInfo.ConfirmPassword}
                        onChange={handleInputChange}
                        onBlur={() => validatePassword()}
                        type="password"
                        placeholder="Confirm your password"
                        error={!!errors['ConfirmPassword']}
                      />
                    </Form.Control>
                  </Form.Field>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-2 mt-2">
                <Button 
                  type="button" 
                  onClick={() => nextPrev(-1)} 
                  disabled={currentTab === 0}
                  variant={currentTab === 0 ? "ghost" : "solid"}
                  className={currentTab === 0 ? "opacity-50" : ""}
                >
                  <ArrowLeft className="mr-1" size={16} />
                  Previous
                </Button>
                
                <Button 
                  type="button" 
                  onClick={() => nextPrev(1)}
                >
                  {currentTab === 3 ? 'Submit' : 'Next'}
                  {currentTab !== 3 && <ArrowRight className="ml-1" size={16} />}
                </Button>
              </div>
            </Form.Root>
          </Tabs.Content>
        </Tabs.Root>

        <div className="flex justify-center mt-6 gap-2">
          {[0, 1, 2, 3].map((step) => (
            <div
              key={step}
              className={`w-3 h-3 rounded-full ${
                currentTab === step
                  ? 'bg-[var(--theme-accent)]'
                  : currentTab > step
                    ? 'bg-[var(--theme-accent-border)]'
                    : 'bg-[var(--theme-line)]'
              }`}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

export default RegistrationForm;